import StipplLogo from "components/website/img/stippl-logo.svg";
import { Fragment, useRef } from "react";
import { cx, MarkerIcon } from "../shared/markers";
import ContactRow from "./ContactRow";
import Disclaimer from "./Disclaimer";
import { mouseHover } from "./hover";
import { UiIcon } from "./Icon";
import { kmFixed, strings } from "./i18n";
import InfoLinks from "./Info";
import SisterCard from "./SisterCard";
import { greetingUrl } from "./weekProgram";
import type { PartnerMapContent } from "./types";
import { bikeMinutes, isBike, placeFill, travelMinutes, walkMinutes, WALK_MAX_KM, type MappedPlace } from "./walk";
import styles from "./PartnerMap.module.scss";

/** a highlight this close is "around the corner", a farther one "a short walk" */
const AROUND_THE_CORNER_KM = 1;

interface HighlightsTabProps {
	content: PartnerMapContent;
	places: Record<string, MappedPlace>;
	/** today's "Tonight on ..." phrases, empty when there is nothing to say */
	tonight: string[];
	/** a place's slug: the row of the place that is open */
	active: string | null;
	/** "base" or a place's slug: the row a mouse is over, on the list or on the map */
	hover: string | null;
	onOpenBase: () => void;
	onOpenPlace: (slug: string) => void;
	onHover: (key: string | null) => void;
	onWeek: () => void;
	/** the line under the base card: on to the offers tab */
	onOffers: () => void;
	/** the base card's button of a restaurant or bar: on to the Contact tab */
	onContactTab: () => void;
	onContact: (channel: string) => void;
}

/**
 * The Highlights tab: the base, what is on tonight, the crew's picks by distance (the content's `bands`; else two, or for a partner with a `rideLabel`
 * the ones to walk to and the ones worth a ride), the sister restaurant, the next stops, and how to reach the partner: its contact row, or its `info`.
 */
const HighlightsTab = ({
	content,
	places,
	tonight,
	active,
	hover,
	onOpenBase,
	onOpenPlace,
	onHover,
	onWeek,
	onOffers,
	onContactTab,
	onContact,
}: HighlightsTabProps) => {
	const { partner, base, weekProgram, info, contact, offers, nextStops, rideLabel, bikeFromKm } = content;
	const t = strings(content.lang);
	const timeFirst = content.theme.look === "venue";
	const contactRef = useRef<HTMLDivElement>(null);
	const highlights = content.highlights.map((h) => places[h.slug]);
	// the offers that lead the tab while they are on (the page has left out the ones that are over)
	const promos = (offers?.cards ?? []).flatMap((card) => (card.promo ? [{ card, promo: card.promo }] : []));
	const [nearTitle, farTitle, nearKm] = rideLabel ? [t.walkFromHostel, t.worthTheRide, WALK_MAX_KM] : [t.aroundTheCorner, t.shortWalk, AROUND_THE_CORNER_KM];
	// each band takes what is within its `upToKm` and was not taken by an earlier one; the last takes the rest
	let rest = highlights;
	const bands = (content.bands ?? [{ title: nearTitle, upToKm: nearKm }, { title: farTitle }]).map(({ title, upToKm }) => {
		const list = upToKm === undefined ? rest : rest.filter((p) => p.km <= upToKm);
		rest = rest.filter((p) => !list.includes(p));
		return { title, list };
	});

	const jumpToContact = () =>
		contactRef.current?.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });

	const row = (p: MappedPlace) => (
		<button
			key={p.slug}
			type="button"
			id={`partnermap-place-${p.slug}`}
			className={cx(styles.row, active === p.slug && styles.sel, hover === p.slug && styles.on)}
			onClick={() => onOpenPlace(p.slug)}
			{...mouseHover(onHover, p.slug)}
		>
			{p.photo ? (
				<img className={styles.th} src={p.photo} alt="" loading="lazy" />
			) : (
				<span className={styles.th} style={{ background: placeFill(content.theme, p.kind), color: content.theme.lit ?? "#fff" }}>
					<MarkerIcon name={p.icon} size={24} />
				</span>
			)}
			<span className={styles.nm}>
				<b>{p.name}</b>
				<span>
					<i className={styles.tagdot} />
					{p.label}
				</span>
			</span>
			<span className={styles.km}>
				{timeFirst ? (
					// the venue look's row, as the designer has it: the minutes in bold, the distance under them
					<>
						{travelMinutes(p.km, bikeFromKm)} min
						<small>
							{bikeFromKm !== undefined && `${isBike(p.km, bikeFromKm) ? t.byBike : t.walkWord} · `}
							{kmFixed(p.km, content.lang)} km
						</small>
					</>
				) : (
					<>
						{kmFixed(p.km, content.lang)} km
						<small>
							{isBike(p.km, bikeFromKm) ? (
								t.minBike(bikeMinutes(p.km))
							) : rideLabel && p.km > nearKm ? (
								rideLabel
							) : (
								<>
									{walkMinutes(p.km)}
									{t.minWalkSuffix}
								</>
							)}
						</small>
					</>
				)}
			</span>
		</button>
	);

	return (
		<>
			<div className={styles.basecard}>
				<button type="button" id="partnermap-base" className={styles.cardHit} aria-label={partner.name} onClick={onOpenBase} />
				<img className={styles.ph} src={base.photos[0]} alt="" />
				<div className={styles.ov} />
				<span className={styles.logo}>
					<img src={partner.logo} alt="" />
				</span>
				{info ? (
					<button type="button" id="partnermap-book-info" className={styles.contact} onClick={onOpenBase}>
						<MarkerIcon name="bed" size={16} />
						{t.bookInfo}
					</button>
				) : contact ? (
					<button type="button" id="partnermap-contact-tab" className={styles.contact} onClick={onContactTab}>
						<UiIcon name="phone" />
						{t.tabs.contact}
					</button>
				) : (
					<button type="button" id="partnermap-contact-us" className={styles.contact} onClick={jumpToContact}>
						<UiIcon name="chat" />
						{t.contactUs}
					</button>
				)}
				<div className={styles.tx}>
					<span className={styles.eyebrow}>{base.eyebrow ?? t.yourBase}</span>
					<b>{partner.name}</b>
					<small>{base.tagline}</small>
				</div>
			</div>
			{promos.map(({ card, promo }) => (
				<article key={card.id} className={styles.promo} id={`partnermap-promo-${card.id}`}>
					<img src={card.photo} alt="" loading="lazy" style={card.pos ? { objectPosition: card.pos } : undefined} />
					<div className={styles.promoBd}>
						<small>{promo.eyebrow}</small>
						<b>{card.title}</b>
						<p>{promo.text}</p>
						<span className={styles.promoPrice}>{promo.price}</span>
						<div className={styles.promoActs}>
							{contact && (
								<a
									id={`partnermap-promo-${card.id}-book`}
									className={styles.promoBook}
									href={contact.book}
									target="_blank"
									rel="noopener"
									onClick={() => onContact(`promo_${card.id}_book`)}
								>
									{promo.book}
								</a>
							)}
							<a
								id={`partnermap-promo-${card.id}-more`}
								className={styles.promoMore}
								href={card.link}
								target="_blank"
								rel="noopener"
								onClick={() => onContact(`promo_${card.id}_more`)}
							>
								{promo.more}
							</a>
						</div>
					</div>
				</article>
			))}
			{weekProgram?.tonightLabel && tonight.length > 0 && (
				<button type="button" id="partnermap-tonight" className={styles.tonight} onClick={onWeek}>
					<span className={styles.dot} />
					<span>
						<b>{weekProgram.tonightLabel}</b>{" "}
						{tonight.map((phrase, i) => (
							<Fragment key={phrase}>
								{i > 0 && " "}
								<span className={styles.phrase}>{i < tonight.length - 1 ? `${phrase},` : phrase}</span>
							</Fragment>
						))}
					</span>
					<u>{t.weekProgram}</u>
				</button>
			)}
			{offers && (
				<button type="button" id="partnermap-teaser" className={styles.tonight} onClick={onOffers}>
					<span className={styles.dot} />
					<span>
						<b>{offers.teaser.lead}</b>
						{` · ${offers.teaser.text}`}
					</span>
					<u>{t.seeAll}</u>
				</button>
			)}
			{bands.map(
				({ title, list }) =>
					list.length > 0 && (
						<Fragment key={title}>
							<div className={styles.sec}>
								{`${title} `}
								<i>{list.length}</i>
							</div>
							{list.map(row)}
						</Fragment>
					)
			)}
			{contact?.sister && (
				<>
					<div className={styles.sec}>{t.sisterSection}</div>
					<SisterCard content={content} onOpen={onOpenPlace} onContact={onContact} />
				</>
			)}
			{nextStops && nextStops.stops.length > 0 && (
				<div className={styles.nextmm}>
					<h3>
						{nextStops.lead} <em>{nextStops.em}</em>
					</h3>
					<p>{nextStops.subtitle}</p>
					<div className={styles.nmrow}>
						{nextStops.stops.map((stop) => (
							<a key={stop.name} className={styles.nmm} href={stop.url} target="_blank" rel="noopener" onClick={() => onContact("next_stop")}>
								<img src={stop.photo} alt="" loading="lazy" />
								<div>
									<b>{stop.name}</b>
									<span>{stop.line}</span>
									<small>{stop.distance}</small>
									<u>{t.bookBed}</u>
								</div>
							</a>
						))}
					</div>
					{nextStops.plan && (
						<a
							id="partnermap-next-route"
							className={styles.nmplan}
							href={nextStops.plan.url}
							target="_blank"
							rel="noopener"
							onClick={() => onContact("route_plan")}
						>
							<span>
								<b>{nextStops.plan.lead}</b> {nextStops.plan.text}
							</span>
							<i>
								<img src={StipplLogo} alt="Stippl" />
							</i>
						</a>
					)}
				</div>
			)}
			<div className={styles.sec} ref={contactRef} style={{ scrollMarginTop: 14 }}>
				{contact ? t.bookAndContact : info ? partner.name : t.contactUs}
			</div>
			{contact ? (
				<ContactRow content={content} contact={contact} onContact={onContact} />
			) : info ? (
				<InfoLinks info={info} shortName={partner.shortName} idPrefix="partnermap-info" lang={content.lang} onContact={onContact} />
			) : (
				<div className={styles.quick}>
					<a
						id="partnermap-contact-whatsapp"
						className={styles.whatsapp}
						href={greetingUrl(content)}
						target="_blank"
						rel="noopener"
						onClick={() => onContact("whatsapp")}
					>
						<i>
							<UiIcon name="whatsapp" />
						</i>
						{t.whatsapp}
					</a>
					<a id="partnermap-contact-call" href={`tel:${partner.tel}`} onClick={() => onContact("call")}>
						<i>
							<UiIcon name="phone" />
						</i>
						{t.call}
					</a>
					<a id="partnermap-contact-website" href={partner.url} target="_blank" rel="noopener" onClick={() => onContact("website")}>
						<i>
							<UiIcon name="globe" />
						</i>
						{t.website}
					</a>
					<a id="partnermap-contact-directions" href={base.directionsUrl} target="_blank" rel="noopener" onClick={() => onContact("directions")}>
						<i>
							<UiIcon name="directions" />
						</i>
						{t.directions}
					</a>
				</div>
			)}
			<Disclaimer content={content} />
		</>
	);
};

export default HighlightsTab;
